/**
 * IDValidationsSection — Manage reference format rules (booking_number, bl_number, etc.)
 * with CRUD operations and an interactive test panel.
 *
 * Lives inside Platform Settings → extraction category.
 * Uses /admin/reference-rules endpoints directly (fire-and-forget pattern like DocumentCheckMatrix).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Plus,
    Trash01,
    Edit03,
    CheckCircle,
    XCircle,
    AlertTriangle,
    Play,
    ChevronDown,
    ChevronUp,
} from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { Select } from "@/components/base/select/select";
import { apiGet, apiPost, apiPut, apiDelete } from "@/lib/api-client";
import { toast } from "@/lib/toast-service";
import { useCompanies } from "@/hooks/api/use-companies";

// ── Types ──

interface ReferenceRule {
    rule_id: string;
    ref_type: string;
    name: string;
    description: string | null;
    company_id: string | null;
    patterns: {
        accept?: string[];
        reject?: string[];
        strip_prefixes?: string[];
        min_length?: number | null;
        max_length?: number | null;
    };
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

interface TestResult {
    valid: boolean;
    original_value: string;
    cleaned_value: string;
    ref_type: string;
    matched_pattern: string | null;
    rejection_reason: string | null;
    steps: string[];
}

// ── Constants ──

const REF_TYPES = [
    { id: "booking_number", label: "Booking Number" },
    { id: "container_number", label: "Container Number" },
    { id: "bl_number", label: "Bill of Lading" },
    { id: "purchase_order_number", label: "Purchase Order" },
    { id: "internal_reference", label: "Internal Reference" },
];

const EMPTY_PATTERNS = {
    accept: [] as string[],
    reject: [] as string[],
    strip_prefixes: [] as string[],
    min_length: null as number | null,
    max_length: null as number | null,
};

// ── Component ──

export function IDValidationsSection() {
    const { data: companies = [] } = useCompanies();
    const [rules, setRules] = useState<ReferenceRule[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedRules, setExpandedRules] = useState<Set<string>>(new Set());

    // Test panel state
    const [testRefType, setTestRefType] = useState(REF_TYPES[0].id);
    const [testValue, setTestValue] = useState("");
    const [testResult, setTestResult] = useState<TestResult | null>(null);
    const [testLoading, setTestLoading] = useState(false);

    // Edit/create state
    const [editingRule, setEditingRule] = useState<string | null>(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [formData, setFormData] = useState({
        ref_type: REF_TYPES[0].id,
        name: "",
        description: "",
        company_id: null as string | null,
        patterns: { ...EMPTY_PATTERNS },
    });
    const [patternInput, setPatternInput] = useState({ accept: "", reject: "", strip_prefix: "" });
    const [regexError, setRegexError] = useState<string | null>(null);

    // ── Data fetching ──

    const fetchRules = useCallback(async () => {
        try {
            const data = await apiGet<ReferenceRule[]>("/admin/reference-rules");
            setRules(data);
        } catch {
            // 404 = no rules yet, that's fine
            setRules([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    // ── Group rules by ref_type ──

    const rulesByType = useMemo(() => {
        const grouped: Record<string, ReferenceRule[]> = {};
        for (const rt of REF_TYPES) {
            grouped[rt.id] = [];
        }
        for (const rule of rules) {
            if (!grouped[rule.ref_type]) {
                grouped[rule.ref_type] = [];
            }
            grouped[rule.ref_type].push(rule);
        }
        return grouped;
    }, [rules]);

    // ── Test handler ──

    const handleTest = useCallback(async () => {
        if (!testValue.trim()) return;
        setTestLoading(true);
        setTestResult(null);
        try {
            const result = await apiPost<TestResult>("/admin/reference-rules/test", {
                ref_type: testRefType,
                value: testValue.trim(),
            });
            setTestResult(result);
        } catch (err: unknown) {
            const detail = err && typeof err === "object" && "detail" in err
                ? (err as { detail: string }).detail
                : "Test failed";
            toast.error(detail);
        } finally {
            setTestLoading(false);
        }
    }, [testRefType, testValue]);

    // ── Regex validation (client-side for instant feedback) ──

    const validateRegex = useCallback((pattern: string): string | null => {
        if (!pattern.trim()) return null;
        try {
            new RegExp(pattern);
            return null;
        } catch (e) {
            return (e as Error).message;
        }
    }, []);

    // ── Pattern list handlers ──

    const addPattern = useCallback(
        (type: "accept" | "reject" | "strip_prefixes", value: string) => {
            if (!value.trim()) return;
            if (type !== "strip_prefixes") {
                const error = validateRegex(value);
                if (error) {
                    setRegexError(error);
                    return;
                }
            }
            setRegexError(null);
            setFormData((prev) => ({
                ...prev,
                patterns: {
                    ...prev.patterns,
                    [type]: [...(prev.patterns[type] || []), value.trim()],
                },
            }));
            setPatternInput((prev) => ({
                ...prev,
                [type === "strip_prefixes" ? "strip_prefix" : type]: "",
            }));
        },
        [validateRegex]
    );

    const removePattern = useCallback(
        (type: "accept" | "reject" | "strip_prefixes", index: number) => {
            setFormData((prev) => ({
                ...prev,
                patterns: {
                    ...prev.patterns,
                    [type]: (prev.patterns[type] || []).filter((_, i) => i !== index),
                },
            }));
        },
        []
    );

    // ── CRUD handlers ──

    const handleCreate = useCallback(async () => {
        if (!formData.name.trim()) {
            toast.error("Rule name is required");
            return;
        }
        try {
            const created = await apiPost<ReferenceRule>("/admin/reference-rules", {
                ref_type: formData.ref_type,
                name: formData.name,
                description: formData.description || null,
                company_id: formData.company_id || null,
                patterns: formData.patterns,
            });
            setRules((prev) => [...prev, created]);
            setShowCreateForm(false);
            resetForm();
            toast.success("Rule created");
        } catch {
            toast.error("Failed to create rule");
        }
    }, [formData]);

    const handleUpdate = useCallback(
        async (ruleId: string) => {
            try {
                const updated = await apiPut<ReferenceRule>(
                    `/admin/reference-rules/${ruleId}`,
                    {
                        name: formData.name,
                        description: formData.description || null,
                        company_id: formData.company_id || null,
                        patterns: formData.patterns,
                    }
                );
                setRules((prev) =>
                    prev.map((r) => (r.rule_id === ruleId ? updated : r))
                );
                setEditingRule(null);
                resetForm();
                toast.success("Rule updated");
            } catch {
                toast.error("Failed to update rule");
            }
        },
        [formData]
    );

    const handleDelete = useCallback(async (ruleId: string) => {
        try {
            await apiDelete(`/admin/reference-rules/${ruleId}`);
            setRules((prev) => prev.filter((r) => r.rule_id !== ruleId));
            toast.success("Rule deleted");
        } catch {
            toast.error("Failed to delete rule");
        }
    }, []);

    const startEdit = useCallback((rule: ReferenceRule) => {
        setEditingRule(rule.rule_id);
        setShowCreateForm(false);
        setFormData({
            ref_type: rule.ref_type,
            name: rule.name,
            description: rule.description || "",
            company_id: rule.company_id,
            patterns: {
                accept: rule.patterns.accept || [],
                reject: rule.patterns.reject || [],
                strip_prefixes: rule.patterns.strip_prefixes || [],
                min_length: rule.patterns.min_length ?? null,
                max_length: rule.patterns.max_length ?? null,
            },
        });
        setPatternInput({ accept: "", reject: "", strip_prefix: "" });
        setRegexError(null);
    }, []);

    const resetForm = useCallback(() => {
        setFormData({
            ref_type: REF_TYPES[0].id,
            name: "",
            description: "",
            company_id: null,
            patterns: { ...EMPTY_PATTERNS },
        });
        setPatternInput({ accept: "", reject: "", strip_prefix: "" });
        setRegexError(null);
    }, []);

    const toggleExpand = useCallback((ruleId: string) => {
        setExpandedRules((prev) => {
            const next = new Set(prev);
            if (next.has(ruleId)) next.delete(ruleId);
            else next.add(ruleId);
            return next;
        });
    }, []);

    // ── Render ──

    return (
        <div className="mt-6 space-y-4">
            {/* Section header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-sm font-semibold text-primary">
                        ID Validations
                    </h3>
                    <p className="text-xs text-tertiary mt-0.5">
                        Regex rules that control which booking numbers, BL numbers,
                        and container references are accepted or rejected during extraction.
                    </p>
                </div>
                <Button
                    color="secondary"
                    size="sm"
                    iconLeading={Plus}
                    onClick={() => {
                        setShowCreateForm(true);
                        setEditingRule(null);
                        resetForm();
                    }}
                >
                    Add Rule
                </Button>
            </div>

            {/* ── Test Panel ── */}
            <div className="rounded-lg border border-secondary bg-secondary/30 p-4 space-y-3">
                <p className="text-sm font-medium text-primary">
                    Test a Reference
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="w-48 shrink-0">
                        <Select
                            items={REF_TYPES}
                            selectedKey={testRefType}
                            onSelectionChange={(key) => {
                                setTestRefType(key as string);
                                setTestResult(null);
                            }}
                            size="sm"
                            aria-label="Reference type"
                        >
                            {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                        </Select>
                    </div>
                    <div className="flex flex-1 gap-2">
                        <input
                            type="text"
                            value={testValue}
                            onChange={(e) => {
                                setTestValue(e.target.value);
                                setTestResult(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleTest();
                            }}
                            placeholder="e.g. MBANB/26/00026, MAEU1234567, GEL-14-032026"
                            className="flex-1 rounded-lg border-none bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary ring-inset placeholder:text-placeholder focus:ring-2 focus:ring-brand focus:outline-hidden"
                        />
                        <Button
                            color="primary"
                            size="sm"
                            iconLeading={Play}
                            onClick={handleTest}
                            isDisabled={!testValue.trim()}
                            isLoading={testLoading}
                        >
                            Test
                        </Button>
                    </div>
                </div>

                {/* Test result */}
                {testResult && (
                    <div
                        className={`rounded-lg border p-3 space-y-2 ${
                            testResult.valid
                                ? "border-utility-success-200 bg-utility-success-50"
                                : "border-utility-error-200 bg-utility-error-50"
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            {testResult.valid ? (
                                <CheckCircle className="size-4 text-utility-success-600" />
                            ) : (
                                <XCircle className="size-4 text-utility-error-600" />
                            )}
                            <span
                                className={`text-sm font-semibold ${
                                    testResult.valid
                                        ? "text-utility-success-700"
                                        : "text-utility-error-700"
                                }`}
                            >
                                {testResult.valid ? "ACCEPTED" : "REJECTED"}
                            </span>
                            {testResult.matched_pattern && (
                                <Badge color="success" size="sm">
                                    Pattern: {testResult.matched_pattern}
                                </Badge>
                            )}
                            {testResult.rejection_reason && (
                                <Badge color="error" size="sm">
                                    {testResult.rejection_reason}
                                </Badge>
                            )}
                        </div>
                        {testResult.cleaned_value !== testResult.original_value && (
                            <p className="text-xs text-tertiary">
                                Cleaned: <code className="font-mono bg-primary/50 px-1 rounded">{testResult.cleaned_value}</code>
                            </p>
                        )}
                        {/* Step-by-step trace */}
                        <div className="space-y-0.5">
                            {testResult.steps.map((step, i) => (
                                <p key={i} className="text-xs text-tertiary font-mono">
                                    {step.includes("REJECTED") ? (
                                        <span className="text-utility-error-600">{step}</span>
                                    ) : step.includes("Matched accept") ? (
                                        <span className="text-utility-success-600">{step}</span>
                                    ) : (
                                        step
                                    )}
                                </p>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ── Create form ── */}
            {showCreateForm && (
                <RuleForm
                    formData={formData}
                    setFormData={setFormData}
                    patternInput={patternInput}
                    setPatternInput={setPatternInput}
                    regexError={regexError}
                    addPattern={addPattern}
                    removePattern={removePattern}
                    validateRegex={validateRegex}
                    setRegexError={setRegexError}
                    onSave={handleCreate}
                    onCancel={() => {
                        setShowCreateForm(false);
                        resetForm();
                    }}
                    isNew
                    companies={companies}
                />
            )}

            {/* ── Rules by type ── */}
            {loading ? (
                <p className="text-sm text-tertiary py-4 text-center">Loading rules...</p>
            ) : rules.length === 0 && !showCreateForm ? (
                <div className="rounded-lg border border-dashed border-secondary p-6 text-center">
                    <AlertTriangle className="size-5 text-fg-quaternary mx-auto mb-2" />
                    <p className="text-sm text-tertiary">
                        No reference validation rules configured.
                        Using hardcoded defaults.
                    </p>
                    <p className="text-xs text-quaternary mt-1">
                        Add rules to customize which references are accepted for each type.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {REF_TYPES.map((rt) => {
                        const typeRules = rulesByType[rt.id] || [];
                        if (typeRules.length === 0) return null;
                        return (
                            <div key={rt.id}>
                                <p className="text-xs font-semibold text-tertiary uppercase tracking-wider mb-1.5">
                                    {rt.label}
                                    <Badge color="gray" size="sm" className="ml-2">
                                        {typeRules.length}
                                    </Badge>
                                </p>
                                <div className="space-y-2">
                                    {typeRules.map((rule) => (
                                        <div
                                            key={rule.rule_id}
                                            className="rounded-lg border border-secondary overflow-hidden"
                                        >
                                            {editingRule === rule.rule_id ? (
                                                <div className="p-3">
                                                    <RuleForm
                                                        formData={formData}
                                                        setFormData={setFormData}
                                                        patternInput={patternInput}
                                                        setPatternInput={setPatternInput}
                                                        regexError={regexError}
                                                        addPattern={addPattern}
                                                        removePattern={removePattern}
                                                        validateRegex={validateRegex}
                                                        setRegexError={setRegexError}
                                                        onSave={() => handleUpdate(rule.rule_id)}
                                                        onCancel={() => {
                                                            setEditingRule(null);
                                                            resetForm();
                                                        }}
                                                        isNew={false}
                                                        companies={companies}
                                                    />
                                                </div>
                                            ) : (
                                                <RuleRow
                                                    rule={rule}
                                                    companyName={
                                                        rule.company_id
                                                            ? companies.find((c) => c.company_id === rule.company_id)?.company_name ?? null
                                                            : null
                                                    }
                                                    isExpanded={expandedRules.has(rule.rule_id)}
                                                    onToggle={() => toggleExpand(rule.rule_id)}
                                                    onEdit={() => startEdit(rule)}
                                                    onDelete={() => handleDelete(rule.rule_id)}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ── RuleRow: collapsed/expanded view of a single rule ──

function RuleRow({
    rule,
    companyName,
    isExpanded,
    onToggle,
    onEdit,
    onDelete,
}: {
    rule: ReferenceRule;
    companyName: string | null;
    isExpanded: boolean;
    onToggle: () => void;
    onEdit: () => void;
    onDelete: () => void;
}) {
    const acceptCount = rule.patterns.accept?.length || 0;
    const rejectCount = rule.patterns.reject?.length || 0;
    const ChevronIcon = isExpanded ? ChevronUp : ChevronDown;

    return (
        <>
            <div className="flex items-center justify-between px-4 py-2.5 bg-primary hover:bg-primary_hover transition-colors">
                <button
                    type="button"
                    className="flex flex-1 items-center gap-2 text-left focus:outline-hidden"
                    onClick={onToggle}
                >
                    <ChevronIcon className="size-4 text-fg-quaternary shrink-0" />
                    <span className="text-sm font-medium text-primary">
                        {rule.name}
                    </span>
                    {rule.description && (
                        <span className="text-xs text-tertiary truncate hidden sm:inline">
                            — {rule.description}
                        </span>
                    )}
                    <div className="flex gap-1.5 ml-auto mr-2">
                        <Badge color={rule.company_id ? "blue" : "gray"} size="sm">
                            {rule.company_id ? companyName || "Company" : "Global"}
                        </Badge>
                        {acceptCount > 0 && (
                            <Badge color="success" size="sm">
                                {acceptCount} accept
                            </Badge>
                        )}
                        {rejectCount > 0 && (
                            <Badge color="error" size="sm">
                                {rejectCount} reject
                            </Badge>
                        )}
                    </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                    <Button
                        color="tertiary"
                        size="sm"
                        iconLeading={Edit03}
                        onClick={onEdit}
                    />
                    <Button
                        color="tertiary"
                        size="sm"
                        iconLeading={Trash01}
                        onClick={onDelete}
                    />
                </div>
            </div>

            {isExpanded && (
                <div className="border-t border-secondary px-4 py-3 bg-secondary/20 space-y-2">
                    <PatternList label="Accept patterns" patterns={rule.patterns.accept} color="success" />
                    <PatternList label="Reject patterns" patterns={rule.patterns.reject} color="error" />
                    {(rule.patterns.strip_prefixes?.length ?? 0) > 0 && (
                        <PatternList
                            label="Strip prefixes"
                            patterns={rule.patterns.strip_prefixes}
                            color="gray"
                        />
                    )}
                    <div className="flex gap-4 text-xs text-tertiary">
                        {rule.patterns.min_length != null && (
                            <span>Min length: <strong>{rule.patterns.min_length}</strong></span>
                        )}
                        {rule.patterns.max_length != null && (
                            <span>Max length: <strong>{rule.patterns.max_length}</strong></span>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

// ── PatternList: display a list of regex patterns with badges ──

function PatternList({
    label,
    patterns,
    color,
}: {
    label: string;
    patterns?: string[];
    color: "success" | "error" | "gray";
}) {
    if (!patterns || patterns.length === 0) return null;
    return (
        <div>
            <p className="text-xs font-medium text-tertiary mb-1">{label}</p>
            <div className="flex flex-wrap gap-1">
                {patterns.map((p, i) => (
                    <code
                        key={i}
                        className={`text-[11px] font-mono px-1.5 py-0.5 rounded ${
                            color === "success"
                                ? "bg-utility-success-50 text-utility-success-700"
                                : color === "error"
                                  ? "bg-utility-error-50 text-utility-error-700"
                                  : "bg-secondary text-secondary"
                        }`}
                    >
                        {p}
                    </code>
                ))}
            </div>
        </div>
    );
}

// ── RuleForm: shared create/edit form ──

function RuleForm({
    formData,
    setFormData,
    patternInput,
    setPatternInput,
    regexError,
    addPattern,
    removePattern,
    validateRegex,
    setRegexError,
    onSave,
    onCancel,
    isNew,
    companies,
}: {
    formData: {
        ref_type: string;
        name: string;
        description: string;
        company_id: string | null;
        patterns: {
            accept: string[];
            reject: string[];
            strip_prefixes: string[];
            min_length: number | null;
            max_length: number | null;
        };
    };
    setFormData: React.Dispatch<React.SetStateAction<typeof formData>>;
    patternInput: { accept: string; reject: string; strip_prefix: string };
    setPatternInput: React.Dispatch<React.SetStateAction<typeof patternInput>>;
    regexError: string | null;
    addPattern: (type: "accept" | "reject" | "strip_prefixes", value: string) => void;
    removePattern: (type: "accept" | "reject" | "strip_prefixes", index: number) => void;
    validateRegex: (pattern: string) => string | null;
    setRegexError: (error: string | null) => void;
    onSave: () => void;
    onCancel: () => void;
    isNew: boolean;
    companies: { company_id: string; company_name: string }[];
}) {
    const scopeItems = [
        { id: "_global", label: "Global (all companies)" },
        ...companies.map((c) => ({ id: c.company_id, label: c.company_name })),
    ];
    return (
        <div className="rounded-lg border border-brand bg-primary p-4 space-y-3">
            <p className="text-sm font-semibold text-primary">
                {isNew ? "New Rule" : "Edit Rule"}
            </p>

            {/* Basic fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {isNew && (
                    <div>
                        <Select
                            label="Reference Type"
                            items={REF_TYPES}
                            selectedKey={formData.ref_type}
                            onSelectionChange={(key) =>
                                setFormData((prev) => ({
                                    ...prev,
                                    ref_type: key as string,
                                }))
                            }
                            size="sm"
                        >
                            {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                        </Select>
                    </div>
                )}
                <div>
                    <label className="text-xs font-medium text-secondary mb-1 block">
                        Rule Name
                    </label>
                    <input
                        type="text"
                        value={formData.name}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                name: e.target.value,
                            }))
                        }
                        placeholder="e.g. JamboFresh Booking Format"
                        className="w-full rounded-lg border-none bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary ring-inset placeholder:text-placeholder focus:ring-2 focus:ring-brand focus:outline-hidden"
                    />
                </div>
                <div className={isNew ? "" : "sm:col-span-2"}>
                    <label className="text-xs font-medium text-secondary mb-1 block">
                        Description
                    </label>
                    <input
                        type="text"
                        value={formData.description}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                description: e.target.value,
                            }))
                        }
                        placeholder="Optional description"
                        className="w-full rounded-lg border-none bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary ring-inset placeholder:text-placeholder focus:ring-2 focus:ring-brand focus:outline-hidden"
                    />
                </div>
            </div>

            {/* Scope: Global or company-specific */}
            <div>
                <Select
                    label="Scope"
                    items={scopeItems}
                    selectedKey={formData.company_id || "_global"}
                    onSelectionChange={(key) =>
                        setFormData((prev) => ({
                            ...prev,
                            company_id: key === "_global" ? null : (key as string),
                        }))
                    }
                    size="sm"
                >
                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                </Select>
            </div>

            {/* Length constraints */}
            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className="text-xs font-medium text-secondary mb-1 block">
                        Min Length
                    </label>
                    <input
                        type="number"
                        value={formData.patterns.min_length ?? ""}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                patterns: {
                                    ...prev.patterns,
                                    min_length: e.target.value
                                        ? parseInt(e.target.value)
                                        : null,
                                },
                            }))
                        }
                        placeholder="—"
                        className="w-full rounded-lg border-none bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary ring-inset placeholder:text-placeholder focus:ring-2 focus:ring-brand focus:outline-hidden"
                    />
                </div>
                <div>
                    <label className="text-xs font-medium text-secondary mb-1 block">
                        Max Length
                    </label>
                    <input
                        type="number"
                        value={formData.patterns.max_length ?? ""}
                        onChange={(e) =>
                            setFormData((prev) => ({
                                ...prev,
                                patterns: {
                                    ...prev.patterns,
                                    max_length: e.target.value
                                        ? parseInt(e.target.value)
                                        : null,
                                },
                            }))
                        }
                        placeholder="—"
                        className="w-full rounded-lg border-none bg-primary px-3 py-2 text-sm text-primary shadow-xs ring-1 ring-primary ring-inset placeholder:text-placeholder focus:ring-2 focus:ring-brand focus:outline-hidden"
                    />
                </div>
            </div>

            {/* Regex error */}
            {regexError && (
                <div className="rounded border border-utility-error-200 bg-utility-error-50 px-3 py-2 text-xs text-utility-error-700 font-mono">
                    Invalid regex: {regexError}
                </div>
            )}

            {/* Accept patterns */}
            <PatternEditor
                label="Accept Patterns"
                hint="References must match at least one accept pattern to be valid"
                patterns={formData.patterns.accept || []}
                inputValue={patternInput.accept}
                onInputChange={(v) => {
                    setPatternInput((prev) => ({ ...prev, accept: v }));
                    setRegexError(validateRegex(v));
                }}
                onAdd={() => addPattern("accept", patternInput.accept)}
                onRemove={(i) => removePattern("accept", i)}
                color="success"
                placeholder={String.raw`e.g. ^\d{6,10}$`}
            />

            {/* Reject patterns */}
            <PatternEditor
                label="Reject Patterns"
                hint="References matching any reject pattern are immediately rejected"
                patterns={formData.patterns.reject || []}
                inputValue={patternInput.reject}
                onInputChange={(v) => {
                    setPatternInput((prev) => ({ ...prev, reject: v }));
                    setRegexError(validateRegex(v));
                }}
                onAdd={() => addPattern("reject", patternInput.reject)}
                onRemove={(i) => removePattern("reject", i)}
                color="error"
                placeholder={String.raw`e.g. [*=+@#$%^&!?]`}
            />

            {/* Strip prefixes */}
            <PatternEditor
                label="Strip Prefixes"
                hint="Prefixes removed before validation (plain text, not regex)"
                patterns={formData.patterns.strip_prefixes || []}
                inputValue={patternInput.strip_prefix}
                onInputChange={(v) =>
                    setPatternInput((prev) => ({
                        ...prev,
                        strip_prefix: v,
                    }))
                }
                onAdd={() =>
                    addPattern("strip_prefixes", patternInput.strip_prefix)
                }
                onRemove={(i) => removePattern("strip_prefixes", i)}
                color="gray"
                placeholder="e.g. BKG "
            />

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
                <Button color="secondary" size="sm" onClick={onCancel}>
                    Cancel
                </Button>
                <Button color="primary" size="sm" onClick={onSave}>
                    {isNew ? "Create" : "Save"}
                </Button>
            </div>
        </div>
    );
}

// ── PatternEditor: input + chip list for one pattern category ──

function PatternEditor({
    label,
    hint,
    patterns,
    inputValue,
    onInputChange,
    onAdd,
    onRemove,
    color,
    placeholder,
}: {
    label: string;
    hint: string;
    patterns: string[];
    inputValue: string;
    onInputChange: (v: string) => void;
    onAdd: () => void;
    onRemove: (index: number) => void;
    color: "success" | "error" | "gray";
    placeholder: string;
}) {
    const colorClasses = {
        success: "bg-utility-success-50 text-utility-success-700",
        error: "bg-utility-error-50 text-utility-error-700",
        gray: "bg-secondary text-secondary",
    };

    return (
        <div>
            <label className="text-xs font-medium text-secondary mb-0.5 block">
                {label}
            </label>
            <p className="text-[11px] text-quaternary mb-1">{hint}</p>
            {patterns.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                    {patterns.map((p, i) => (
                        <span
                            key={i}
                            className={`inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded ${colorClasses[color]}`}
                        >
                            {p}
                            <button
                                type="button"
                                onClick={() => onRemove(i)}
                                className="hover:opacity-70 leading-none"
                            >
                                &times;
                            </button>
                        </span>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => onInputChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            onAdd();
                        }
                    }}
                    placeholder={placeholder}
                    className="flex-1 rounded-lg border-none bg-primary px-3 py-1.5 text-xs font-mono text-primary shadow-xs ring-1 ring-primary ring-inset placeholder:text-placeholder focus:ring-2 focus:ring-brand focus:outline-hidden"
                />
                <Button
                    color="secondary"
                    size="sm"
                    iconLeading={Plus}
                    onClick={onAdd}
                    isDisabled={!inputValue.trim()}
                />
            </div>
        </div>
    );
}
